import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatINR } from "@/components/ChefCard";
import { FileCheck2, Download, Eraser } from "lucide-react";

function SignaturePad({ canvasRef }) {
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
  }, [canvasRef]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.beginPath();
  };

  return (
    <div>
      <canvas
        ref={canvasRef} width={560} height={180}
        data-testid="signature-canvas"
        className="w-full bg-white border border-white/20 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button onClick={clear} data-testid="signature-clear"
        className="mt-2 small-caps text-[10px] text-neutral-400 hover:text-white inline-flex items-center gap-1">
        <Eraser size={12} /> Clear
      </button>
    </div>
  );
}

export default function AgreementDialog({ bookingId, open, onOpenChange, onSigned }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [mode, setMode] = useState("type");
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!open || !bookingId) return;
    (async () => {
      try {
        const r = await api.get(`/bookings/${bookingId}/agreement`);
        setData(r.data);
        setName(r.data.customer_name || "");
      } catch { toast.error("Could not load the agreement"); }
    })();
  }, [open, bookingId]);

  const sign = async () => {
    if (!accepted) return toast.error("Accept the terms to sign");
    if (name.trim().length < 3) return toast.error("Type your full legal name");
    let image = "";
    if (mode === "draw" && canvasRef.current) {
      image = canvasRef.current.toDataURL("image/png");
    }
    setSaving(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/agreement/sign`, {
        signature_name: name.trim(), signature_image: image, accepted: true,
      });
      toast.success(`Agreement ${r.data.agreement.number} signed`);
      onSigned?.(r.data.agreement);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign"); }
    finally { setSaving(false); }
  };

  const e = data?.engagement || {};
  const signed = data?.agreement;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[92vh] overflow-y-auto" data-testid="agreement-dialog">
        <div className="p-8">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl">Engagement agreement</DialogTitle>
            <p className="text-neutral-400 text-sm mt-1">
              11 months, renewable. Signed electronically and kept on file with your booking.
            </p>
          </DialogHeader>

          {!data ? (
            <div className="mt-8 text-neutral-500 text-sm">Loading…</div>
          ) : (
            <>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm border border-white/10 p-5">
                <div className="flex justify-between"><span className="text-neutral-400">Staff member</span><span>{e.chef_name}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">City</span><span>{e.city || "—"}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Start date</span><span>{e.start_date || "—"}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Term</span><span>11 months</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Monthly salary</span><span>₹{formatINR(e.monthly_salary)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Placement fee + GST</span><span className="text-[#F5B041]">₹{formatINR(e.payable)}</span></div>
              </div>

              <div className="mt-6 max-h-64 overflow-y-auto border border-white/10 p-5 space-y-4" data-testid="agreement-clauses">
                {(data.clauses || []).map((c, i) => (
                  <div key={c.heading}>
                    <div className="font-serif-menu text-sm text-white">{i + 1}. {c.heading}</div>
                    <p className="text-neutral-400 text-xs mt-1 leading-relaxed">{c.text}</p>
                  </div>
                ))}
              </div>

              {signed ? (
                <div className="mt-6 border border-[#F5B041]/30 bg-[#F5B041]/5 p-5 flex items-center justify-between gap-4 flex-wrap"
                  data-testid="agreement-signed-state">
                  <div className="flex items-center gap-3 text-sm">
                    <FileCheck2 size={18} className="text-[#F5B041]" />
                    <div>
                      <div className="text-white">Signed by {signed.signature_name}</div>
                      <div className="text-neutral-500 text-xs">{signed.number} · {(signed.signed_at || "").slice(0, 10)}</div>
                    </div>
                  </div>
                  <a href={signed.url} target="_blank" rel="noreferrer" data-testid="agreement-download"
                    className="btn-primary small-caps text-[10px] inline-flex items-center gap-2">
                    <Download size={13} /> Download PDF
                  </a>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <Tabs value={mode} onValueChange={setMode}>
                    <TabsList className="rounded-none bg-transparent border border-white/10 h-10 p-0">
                      <TabsTrigger value="type" data-testid="sign-mode-type" className="rounded-none small-caps text-[10px] h-full px-5 data-[state=active]:bg-white/5">Type name</TabsTrigger>
                      <TabsTrigger value="draw" data-testid="sign-mode-draw" className="rounded-none small-caps text-[10px] h-full px-5 data-[state=active]:bg-white/5">Draw signature</TabsTrigger>
                    </TabsList>
                    <TabsContent value="type" className="mt-4">
                      <Label className="small-caps text-[10px] text-neutral-500">Full legal name</Label>
                      <input value={name} onChange={(ev) => setName(ev.target.value)} data-testid="signature-name"
                        placeholder="As it should appear on the agreement"
                        className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                      <div className="mt-3 border-t border-white/10 pt-3">
                        <span className="font-script text-3xl text-white">{name || "Your signature"}</span>
                      </div>
                    </TabsContent>
                    <TabsContent value="draw" className="mt-4 space-y-3">
                      <SignaturePad canvasRef={canvasRef} />
                      <div>
                        <Label className="small-caps text-[10px] text-neutral-500">Full legal name</Label>
                        <input value={name} onChange={(ev) => setName(ev.target.value)} data-testid="signature-name-draw"
                          className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                      </div>
                    </TabsContent>
                  </Tabs>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="agreement-accept"
                      className="rounded-none border-white/30 data-[state=checked]:bg-[#C04000] data-[state=checked]:border-[#C04000] mt-0.5" />
                    <span className="text-xs text-neutral-300">
                      I have read the agreement above and accept it on behalf of my household. I understand this
                      electronic signature is legally binding.
                    </span>
                  </label>

                  <Button onClick={sign} disabled={saving || !accepted} data-testid="agreement-sign-btn"
                    className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
                    {saving ? "Signing…" : "Sign agreement"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
